import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { IMAGES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { Coffee, Check, ArrowRight, Expand, Quote } from "lucide-react";

export default function BreakfastPage() {
  const { t, lang } = useI18n();
  const [lbIndex, setLbIndex] = useState(null);
  const images = IMAGES.breakfastGallery;
  const points = t("home.breakfastPoints");

  return (
    <>
      <section data-testid="breakfast-page" className="pt-36 pb-12 bg-[#F2EFEB]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#345B73] mb-4">
              <Coffee className="w-4 h-4" /> {t("breakfastPage.eyebrow")}
            </div>
            <h1 className="font-display text-5xl md:text-6xl text-[#1A1A1A] tracking-tight">{t("breakfastPage.title")}</h1>
            <p className="mt-5 text-lg text-[#5C5C5C] max-w-3xl leading-relaxed">{t("breakfastPage.intro")}</p>
            <ul className="mt-7 flex flex-wrap gap-3">
              {points.map((p, i) => (
                <li key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#1D3557]/15 text-sm text-[#1A1A1A]">
                  <Check className="w-4 h-4 text-[#345B73] flex-shrink-0" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 auto-rows-[180px] md:auto-rows-[240px] gap-3 md:gap-4">
              {images.map((src, i) => {
                // Make the first and fourth tiles larger for an editorial rhythm
                const big = i === 0 ? "col-span-2 row-span-2" : (i === 3 ? "md:col-span-2" : "");
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setLbIndex(i)}
                    data-testid={`breakfast-gallery-img-${i}`}
                    className={`group relative overflow-hidden rounded-2xl cursor-pointer ${big}`}
                  >
                    <img src={src} alt={`${t("breakfastPage.title")} — ${i + 1}`} loading="lazy"
                      className="w-full h-full object-cover img-zoom" />
                    <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Expand className="w-4 h-4" />
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl text-[#1A1A1A] tracking-tight text-center mb-10">{t("breakfastPage.quotesTitle")}</h2>
            <div className="grid md:grid-cols-2 gap-5 mb-14">
              {t("breakfastPage.quotes").map((q, i) => (
                <div key={i} data-testid={`breakfast-quote-${i}`} className="bg-[#F2EFEB] rounded-2xl p-6 md:p-7 relative">
                  <Quote className="w-7 h-7 text-[#345B73]/30 mb-3" />
                  <p className="text-[#1A1A1A] leading-relaxed italic">“{q.text}”</p>
                  <p className="mt-4 text-sm text-[#5C5C5C] font-medium">{q.author}</p>
                </div>
              ))}
            </div>
            <div className="text-center bg-[#1D3557] text-white rounded-3xl px-8 py-14 md:py-16">
              <h2 className="font-display text-3xl md:text-4xl tracking-tight">{t("breakfastPage.ctaTitle")}</h2>
              <p className="mt-4 text-white/80 max-w-xl mx-auto leading-relaxed">{t("breakfastPage.ctaText")}</p>
              <Link
                to={`/${lang}/prenota`}
                data-testid="breakfast-cta-book"
                className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-[#1D3557] font-medium hover:bg-[#F2EFEB] transition-colors"
              >
                {t("breakfastPage.ctaButton")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Lightbox images={images} index={lbIndex} onIndexChange={setLbIndex}
        onClose={() => setLbIndex(null)} alt={t("breakfastPage.title")} />
    </>
  );
}
